import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { ArrowLeft, Plus, X, Trash2, Clock, Edit2, Check } from 'lucide-react'
import { format } from 'date-fns'
import ProductPicker from '../components/ProductPicker'
import { PO_STATUS_OPTIONS, poBaseStatus, loadOrderStates, DISPLAY } from '../lib/orderStatus'
import HelpLink from '../components/HelpLink'

// Unit costs show up to 4 decimals ($0.7165), totals 2 decimals with thousands separators
const fmtCost = n => Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 })
const fmtMoney = n => Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Timeline steps: Draft → Confirmed → Completed (Completed is worked out from the uploads, never set by hand)
const TIMELINE = ['draft', 'confirmed', 'completed']

export default function PODetail() {
  const { id } = useParams()
  const { activeBrand } = useAuth()
  const navigate = useNavigate()
  const [po, setPO] = useState(null)
  const [lines, setLines] = useState([])
  const [history, setHistory] = useState([])
  const [products, setProducts] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [shipments, setShipments] = useState([])
  const [states, setStates] = useState({ poState: {}, shipmentState: {} })
  const [shipmentLines, setShipmentLines] = useState([])
  const [lineModal, setLineModal] = useState(false)
  const [editLineId, setEditLineId] = useState(null) // null = adding a new line
  const [lineForm, setLineForm] = useState({ product_id: '', description: '', quantity_ordered: '', unit_cost: '' })
  const [saving, setSaving] = useState(false)
  const [editingHeader, setEditingHeader] = useState(false)
  const [headerForm, setHeaderForm] = useState({})
  const [locations, setLocations] = useState([])
  const [shipModal, setShipModal] = useState(false)
  const [shipForm, setShipForm] = useState(null)
  const [shipError, setShipError] = useState('')

  useEffect(() => { load() }, [id])

  async function load() {
    const [{ data: poData }, { data: lineData }, { data: prodData }, { data: histData }, { data: supData }, { data: shipData }, { data: locData }] = await Promise.all([
      supabase.from('purchase_orders').select('*, suppliers(name, payment_terms)').eq('id', id).single(),
      supabase.from('po_line_items').select('*, products(name, sku)').eq('po_id', id),
      activeBrand ? supabase.from('products').select('id, name, sku, cost_price').eq('brand_id', activeBrand.id).is('deleted_at', null).order('sku') : Promise.resolve({ data: [] }),
      supabase.from('po_status_history').select('*, user_profiles(full_name, email)').eq('po_id', id).order('changed_at', { ascending: true }),
      activeBrand ? supabase.from('suppliers').select('id, name').eq('brand_id', activeBrand.id).order('name') : Promise.resolve({ data: [] }),
      supabase.from('shipments').select('*, ship_to_loc:ship_to_location_id(name)').eq('po_id', id).is('deleted_at', null).order('created_at'),
      activeBrand ? supabase.from('locations').select('id, name, type').eq('brand_id', activeBrand.id).eq('is_active', true).order('name') : Promise.resolve({ data: [] }),
    ])
    
    // Load shipment line items for all shipments on this PO
    let shipLineData = []
    if (shipData && shipData.length > 0) {
      const shipIds = shipData.map(s => s.id)
      const { data: sld } = await supabase.from('shipment_line_items').select('product_id, po_line_item_id, quantity_ordered, quantity_shipped, quantity_received, shipment_id').in('shipment_id', shipIds)
      shipLineData = sld || []
    }
    setShipmentLines(shipLineData)
    setPO(poData)
    setLines(lineData || [])
    setProducts(prodData || [])
    setHistory(histData || [])
    setSuppliers(supData || [])
    setShipments(shipData || [])
    setLocations(locData || [])
    if (activeBrand) { try { setStates(await loadOrderStates(activeBrand.id)) } catch (e) { /* labels fall back to stored status */ } }
  }

  async function updateStatus(status) {
    await supabase.from('purchase_orders').update({ status }).eq('id', id)
    load()
  }

  function startEditHeader() {
    setHeaderForm({
      supplier_id: po.supplier_id || '',
      order_date: po.order_date || '',
      expected_date: po.expected_date || '',
      ship_window_start: po.ship_window_start || '',
      ship_window_end: po.ship_window_end || '',
      notes: po.notes || ''
    })
    setEditingHeader(true)
  }

  async function saveHeader() {
    await supabase.from('purchase_orders').update({
      supplier_id: headerForm.supplier_id || null,
      order_date: headerForm.order_date || null,
      expected_date: headerForm.expected_date || null,
      ship_window_start: headerForm.ship_window_start || null,
      ship_window_end: headerForm.ship_window_end || null,
      notes: headerForm.notes || null
    }).eq('id', id)
    setEditingHeader(false)
    load()
  }

  function openNewLine() {
    setEditLineId(null)
    setLineForm({ product_id: '', description: '', quantity_ordered: '', unit_cost: '' })
    setLineModal(true)
  }

  function openEditLine(l) {
    setEditLineId(l.id)
    setLineForm({ product_id: l.product_id || '', description: l.description || '', quantity_ordered: l.quantity_ordered ?? '', unit_cost: l.unit_cost ?? '' })
    setLineModal(true)
  }

  // Adds a line, or saves changes to the line being edited
  async function addLine() {
    setSaving(true)
    const prod = products.find(p => p.id === lineForm.product_id)
    const row = {
      product_id: lineForm.product_id || null,
      description: lineForm.description || prod?.name || '',
      sku: prod?.sku || '',
      quantity_ordered: parseFloat(lineForm.quantity_ordered),
      unit_cost: lineForm.unit_cost !== '' && lineForm.unit_cost != null ? parseFloat(lineForm.unit_cost) : null
    }
    if (editLineId) await supabase.from('po_line_items').update(row).eq('id', editLineId)
    else await supabase.from('po_line_items').insert({ po_id: id, ...row })
    setSaving(false); setLineModal(false); setEditLineId(null)
    setLineForm({ product_id: '', description: '', quantity_ordered: '', unit_cost: '' })
    load()
  }

  async function deleteLine(lid) {
    if (window.confirm('Remove this line item?')) { await supabase.from('po_line_items').delete().eq('id', lid); load() }
  }

  function handleProductSelect(pid) {
    const prod = products.find(p => p.id === pid)
    // Editing: keep the line's own description and cost, only fill blanks from the product
    setLineForm(f => editLineId
      ? { ...f, product_id: pid, description: f.description || prod?.name || '', unit_cost: f.unit_cost !== '' && f.unit_cost != null ? f.unit_cost : prod?.cost_price || '' }
      : { ...f, product_id: pid, description: prod?.name || '', unit_cost: prod?.cost_price || '' })
  }

  // ── shipments created from this PO ──
  const activeShipIds = new Set(shipments.filter(s => s.status !== 'cancelled').map(s => s.id))
  const slUnits = sl => Number(sl.quantity_shipped ?? sl.quantity_ordered ?? 0) || 0
  // units of a PO line already put on (non-cancelled) shipments: matched by PO line, or by product for older lines
  const onShipments = l => shipmentLines.filter(sl => activeShipIds.has(sl.shipment_id) && (sl.po_line_item_id ? sl.po_line_item_id === l.id : sl.product_id && sl.product_id === l.product_id)).reduce((a, sl) => a + slUnits(sl), 0)
  const receivedOf = l => shipmentLines.filter(sl => sl.po_line_item_id ? sl.po_line_item_id === l.id : sl.product_id && sl.product_id === l.product_id).reduce((a, sl) => a + (Number(sl.quantity_received) || 0), 0)

  async function openShipModal() {
    const { data: all } = await supabase.from('shipments').select('shipment_number').eq('brand_id', activeBrand.id).is('deleted_at', null)
    const nums = (all || []).map(x => parseInt(String(x.shipment_number || '').replace(/^SHIP-/, ''), 10)).filter(n => !isNaN(n))
    const next = `SHIP-${String((nums.length ? Math.max(...nums) : 0) + 1).padStart(3, '0')}`
    const wh = locations.find(l => l.type !== 'amazon_fba' && l.type !== 'supplier') || locations[0]
    setShipError('')
    setShipForm({
      shipment_number: next, ship_to_location_id: wh?.id || '', carrier: '', tracking_number: '',
      expected_ship_date: po.ship_window_start || '', expected_arrival: po.expected_date || '', notes: '',
      lines: lines.filter(l => l.product_id).map(l => {
        const left = Math.max(0, Number(l.quantity_ordered) - onShipments(l))
        return { po_line_item_id: l.id, product_id: l.product_id, sku: l.products?.sku || l.sku, name: l.description || l.products?.name, ordered: Number(l.quantity_ordered), left, qty: left ? String(left) : '' }
      }),
    })
    setShipModal(true)
  }

  async function createShipment() {
    const f = shipForm
    const chosen = f.lines.map(l => ({ ...l, q: parseInt(l.qty, 10) || 0 })).filter(l => l.q > 0)
    if (!chosen.length) { setShipError('Enter a quantity for at least one line.'); return }
    setSaving(true); setShipError('')
    const { data: dup } = await supabase.from('shipments').select('id').eq('brand_id', activeBrand.id).eq('shipment_number', f.shipment_number).is('deleted_at', null)
    if (dup?.length) { setShipError('A shipment with this number already exists.'); setSaving(false); return }
    const { data: sh, error } = await supabase.from('shipments').insert({
      brand_id: activeBrand.id, po_id: id, shipment_number: f.shipment_number, supplier_name: po.suppliers?.name || null,
      carrier: f.carrier || null, tracking_number: f.tracking_number || null,
      expected_ship_date: f.expected_ship_date || null, expected_arrival: f.expected_arrival || null,
      status: 'scheduled', notes: f.notes || null, is_direct_ship: false, ship_to_location_id: f.ship_to_location_id || null,
    }).select().single()
    if (error) { setShipError('Save failed: ' + error.message); setSaving(false); return }
    const { error: le } = await supabase.from('shipment_line_items').insert(chosen.map(l => ({
      shipment_id: sh.id, product_id: l.product_id, po_line_item_id: l.po_line_item_id, quantity_ordered: l.q,
    })))
    if (le) { setShipError(`Shipment ${f.shipment_number} created, but adding lines failed: ${le.message}`); setSaving(false); load(); return }
    setSaving(false); setShipModal(false); setShipForm(null)
    load()
  }

  const total = lines.reduce((sum, l) => sum + (l.quantity_ordered * (l.unit_cost || 0)), 0)

  if (!po) return <div className="loading-screen"><div className="spinner" /></div>
  const poState = states.poState[po.id] || poBaseStatus(po.status)

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/purchase-orders')}><ArrowLeft size={14} />Back</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ fontFamily: 'var(--mono)' }}>{po.po_number}<HelpLink section="purchase-orders" /></h1>
              <select className="select" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={poBaseStatus(po.status)} onChange={e => updateStatus(e.target.value)}>
                {PO_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {poState === 'completed' && <span className="badge badge-success" title="Every shipment on this PO has landed in an inventory upload">Completed</span>}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>{po.suppliers?.name}</div>
          </div>
        </div>
        {!editingHeader && (
          <button className="btn btn-secondary btn-sm" onClick={startEditHeader}><Edit2 size={13} />Edit Details</button>
        )}
      </div>

      {/* Header — view or edit mode */}
      {editingHeader ? (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Edit PO Details</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingHeader(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveHeader}><Check size={13} />Save</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Supplier</label>
                <select className="select" value={headerForm.supplier_id} onChange={e => setHeaderForm(f => ({ ...f, supplier_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Order Date</label><input className="input" type="date" value={headerForm.order_date} onChange={e => setHeaderForm(f => ({ ...f, order_date: e.target.value }))} /></div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Ship Window Start</label><input className="input" type="date" value={headerForm.ship_window_start} onChange={e => setHeaderForm(f => ({ ...f, ship_window_start: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Ship Window End</label><input className="input" type="date" value={headerForm.ship_window_end} onChange={e => setHeaderForm(f => ({ ...f, ship_window_end: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Expected Arrival</label><input className="input" type="date" value={headerForm.expected_date} onChange={e => setHeaderForm(f => ({ ...f, expected_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={headerForm.notes} onChange={e => setHeaderForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {[
              { label: 'Supplier', value: po.suppliers?.name || '—' },
              { label: 'Payment Terms', value: po.suppliers?.payment_terms || '—' },
              { label: 'Order Date', value: po.order_date ? format(new Date(po.order_date + 'T12:00:00'), 'MMM d, yyyy') : '—' },
              { label: 'Expected Arrival', value: po.expected_date ? format(new Date(po.expected_date + 'T12:00:00'), 'MMM d, yyyy') : '—' },
            ].map(({ label, value }) => (
              <div className="card" key={label} style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>
          {(po.ship_window_start || po.ship_window_end) && (
            <div className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Ship Window</div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>
                {po.ship_window_start ? format(new Date(po.ship_window_start + 'T12:00:00'), 'MMM d, yyyy') : '—'}{' → '}{po.ship_window_end ? format(new Date(po.ship_window_end + 'T12:00:00'), 'MMM d, yyyy') : '—'}
              </div>
            </div>
          )}
          {po.notes && (
            <div className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Notes</div>
              <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>{po.notes}</p>
            </div>
          )}
        </>
      )}

      {/* Status Timeline */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Clock size={15} style={{ color: 'var(--accent)' }} />
          <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Status Timeline</h3>
        </div>
        <div style={{ display: 'flex' }}>
          {TIMELINE.map((s, i, arr) => {
            const currentOrder = TIMELINE.indexOf(poState)
            const thisOrder = i
            const isDone = poState !== 'cancelled' && thisOrder <= currentOrder
            const isCurrent = s === poState
            const histEntry = s === 'completed' ? null : history.find(h => poBaseStatus(h.status) === s)
            return (
              <div key={s} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                {i > 0 && <div style={{ position: 'absolute', left: 0, top: '12px', right: '50%', height: '2px', background: isDone ? 'var(--accent)' : 'var(--border-2)' }} />}
                {i < arr.length - 1 && <div style={{ position: 'absolute', right: 0, top: '12px', left: '50%', height: '2px', background: isDone && !isCurrent ? 'var(--accent)' : 'var(--border-2)' }} />}
                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: isDone ? 'var(--accent)' : 'var(--bg-4)', border: `2px solid ${isDone ? 'var(--accent)' : 'var(--border-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, fontSize: '10px', color: isDone ? 'white' : 'var(--text-3)', fontWeight: 700 }}>
                  {isDone ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '10px', fontWeight: isCurrent ? 600 : 400, color: isCurrent ? 'var(--accent)' : isDone ? 'var(--text-2)' : 'var(--text-3)', marginTop: '6px', textAlign: 'center' }}>{DISPLAY[s].label}</div>
                {histEntry && <div style={{ fontSize: '9px', color: 'var(--text-3)', marginTop: '2px' }}>{format(new Date(histEntry.changed_at), 'MMM d')}</div>}
              </div>
            )
          })}
        </div>
        {poState === 'completed' && <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '12px' }}>Completed automatically: every shipment on this PO has landed in an inventory upload.</div>}
        {po.status === 'cancelled' && <div className="alert alert-danger" style={{ marginTop: '12px' }}>This PO was cancelled.</div>}
      </div>

      {/* Line Items */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600 }}>Line Items</h3>
          <button className="btn btn-primary btn-sm" onClick={openNewLine}><Plus size={13} />Add Line</button>
        </div>
        {lines.length === 0 ? <div className="empty-state" style={{ padding: '30px' }}><p>No line items yet</p></div> : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>SKU</th><th>Description</th><th>Qty Ordered</th><th>Qty Received</th><th>Unit Cost</th><th>Total</th><th></th></tr></thead>
                <tbody>
                  {lines.map(l => (
                    <tr key={l.id}>
                      <td><span className="mono">{l.products?.sku || l.sku || '—'}</span></td>
                      <td>{l.description || l.products?.name || '—'}</td>
                      <td>{l.quantity_ordered}</td>
                      <td>{l.quantity_received || 0}</td>
                      <td>{l.unit_cost ? fmtCost(l.unit_cost) : '—'}</td>
                      <td>{fmtMoney(l.quantity_ordered * (l.unit_cost || 0))}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-ghost btn-sm" title="Edit line" onClick={() => openEditLine(l)}><Edit2 size={13} /></button>
                        <button className="btn btn-ghost btn-sm" title="Remove line" onClick={() => deleteLine(l.id)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 14px 0', borderTop: '1px solid var(--border)', marginTop: '8px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>PO Total</div>
                <div style={{ fontSize: '22px', fontWeight: 600 }}>{fmtMoney(total)}</div>
              </div>
            </div>
          </>
        )}
      </div>


      {/* Linked Shipments */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600 }}>Shipments ({shipments.length})</h3>
          <button className="btn btn-secondary btn-sm" onClick={openShipModal}><Plus size={13} />New Shipment</button>
        </div>
        {shipments.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: '13px' }}>No shipments linked to this PO yet.</div>
        ) : (
          <>
            {/* PO-level coverage across all shipments */}
            {lines.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Coverage Across All Shipments</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>SKU</th><th>Product</th><th>Ordered</th><th>On Shipments</th><th>Not on a shipment</th><th>Received</th></tr></thead>
                    <tbody>
                      {lines.map(l => {
                        const assigned = onShipments(l)
                        const remaining = Math.max(0, Number(l.quantity_ordered) - assigned)
                        const received = receivedOf(l)
                        return (
                          <tr key={l.id}>
                            <td><span className="mono">{l.products?.sku || l.sku || '—'}</span></td>
                            <td>{l.description || l.products?.name || '—'}</td>
                            <td>{l.quantity_ordered}</td>
                            <td style={{ fontWeight: 500 }}>{assigned || '—'}</td>
                            <td style={{ color: remaining > 0 ? 'var(--warning)' : 'var(--success)', fontWeight: 500 }}>{remaining || '✓ All assigned'}</td>
                            <td style={{ color: received > 0 ? 'var(--success)' : 'var(--text-3)' }}>{received || '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* Shipment list */}
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Shipments</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Shipment #</th><th>Destination</th><th>Type</th><th>Units</th><th>Status</th><th>Exp Arrival</th><th>Actual Arrival</th></tr></thead>
                <tbody>
                  {shipments.map(s => {
                    const dest = s.is_direct_ship ? (s.ship_to_freeform || 'Direct Ship') : (s.ship_to_loc?.name || '—')
                    const units = shipmentLines.filter(sl => sl.shipment_id === s.id).reduce((sum, sl) => sum + slUnits(sl), 0)
                    return (
                      <tr key={s.id}>
                        <td><Link to={`/shipments/${s.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: '13px' }}>{s.shipment_number}</Link></td>
                        <td style={{ fontStyle: s.is_direct_ship ? 'italic' : 'normal', color: s.is_direct_ship ? 'var(--text-2)' : 'var(--text)' }}>{dest}</td>
                        <td><span className={`badge ${s.is_direct_ship ? 'badge-warning' : 'badge-neutral'}`} style={{ fontSize: '10px' }}>{s.is_direct_ship ? 'Direct Ship' : 'To Location'}</span></td>
                        <td style={{ fontWeight: 500 }}>{units || '—'}</td>
                        <td>{(() => { const st = states.shipmentState[s.id] || 'planned'; return <span className={`badge badge-${DISPLAY[st].badge}`}>{DISPLAY[st].label}</span> })()}</td>
                        <td style={{ fontSize: '12px' }}>{s.expected_arrival ? format(new Date(s.expected_arrival + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px' }}>{s.actual_arrival ? format(new Date(s.actual_arrival + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {shipModal && shipForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !saving && setShipModal(false)}>
          <div className="modal modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header"><h2>New Shipment · {po.po_number}</h2><button className="btn btn-ghost btn-sm" onClick={() => setShipModal(false)}><X size={16} /></button></div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {shipError && <div className="alert alert-danger">{shipError}</div>}
              <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>Linked to {po.po_number}{po.suppliers?.name ? ` · ${po.suppliers.name}` : ''}. Quantities start at what isn't on a shipment yet — to split, create the FBA shipment first, then the warehouse one gets the rest.</div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Shipment #</label><input className="input" value={shipForm.shipment_number} onChange={e => setShipForm(f => ({ ...f, shipment_number: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Ship to</label>
                  <select className="select" value={shipForm.ship_to_location_id} onChange={e => setShipForm(f => ({ ...f, ship_to_location_id: e.target.value }))}>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Expected ship date</label><input className="input" type="date" value={shipForm.expected_ship_date} onChange={e => setShipForm(f => ({ ...f, expected_ship_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Expected arrival</label><input className="input" type="date" value={shipForm.expected_arrival} onChange={e => setShipForm(f => ({ ...f, expected_arrival: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Carrier</label><input className="input" placeholder="FedEx, UPS, freight…" value={shipForm.carrier} onChange={e => setShipForm(f => ({ ...f, carrier: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Tracking #</label><input className="input" value={shipForm.tracking_number} onChange={e => setShipForm(f => ({ ...f, tracking_number: e.target.value }))} /></div>
              </div>
              <div className="table-wrap">
                <table style={{ fontSize: '12px' }}>
                  <thead><tr><th>SKU</th><th>Product</th><th style={{ textAlign: 'right' }}>Ordered</th><th style={{ textAlign: 'right' }}>Not on a shipment</th><th style={{ textAlign: 'right', width: '120px' }}>This shipment</th></tr></thead>
                  <tbody>
                    {shipForm.lines.map((l, i) => (
                      <tr key={l.po_line_item_id}>
                        <td className="mono">{l.sku}</td>
                        <td style={{ color: 'var(--text-2)' }}>{l.name}</td>
                        <td style={{ textAlign: 'right' }}>{l.ordered.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{l.left.toLocaleString()}</td>
                        <td><input className="input" type="number" min="0" style={{ textAlign: 'right', padding: '4px 8px' }} value={l.qty} onChange={e => setShipForm(f => ({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)) }))} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={shipForm.notes} onChange={e => setShipForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShipModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={createShipment} disabled={saving || !shipForm.shipment_number}>{saving ? 'Creating…' : 'Create shipment'}</button>
            </div>
          </div>
        </div>
      )}

      {lineModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setLineModal(false)}>
          <div className="modal">
            <div className="modal-header"><h2>{editLineId ? 'Edit Line Item' : 'Add Line Item'}</h2><button className="btn btn-ghost btn-sm" onClick={() => setLineModal(false)}><X size={16} /></button></div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product (optional)</label>
                <ProductPicker
                  products={products}
                  value={lineForm.product_id}
                  onChange={handleProductSelect}
                  placeholder="Search for a product (or leave empty to enter manually)..."
                />
              </div>
              <div className="form-group"><label className="form-label">Description</label><input className="input" value={lineForm.description} onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))} placeholder="Item description" /></div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Quantity *</label><input className="input" type="number" value={lineForm.quantity_ordered} onChange={e => setLineForm(f => ({ ...f, quantity_ordered: e.target.value }))} placeholder="0" /></div>
                <div className="form-group"><label className="form-label">Unit Cost</label><input className="input" type="number" step="any" value={lineForm.unit_cost} onChange={e => setLineForm(f => ({ ...f, unit_cost: e.target.value }))} placeholder="0.00" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLineModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addLine} disabled={saving || !lineForm.quantity_ordered}>{saving ? 'Saving...' : editLineId ? 'Save Line' : 'Add Line'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
